export function pickDefaultVariant(variants) {
  return variants.find((v) => v.available)?.id ?? null;
}

// Pure decision helper: what should a [data-gm-add] click do, given only the
// product's variants? Kept separate from the click handler (and from any DOM
// or network access) so it can be unit tested without a browser.
//   - multi-variant products always send the customer to the PDP so they can
//     choose a colour/size themselves (see brief note: "honest, avoids
//     adding a random colour"). This holds even if every variant is sold
//     out — the PDP is the right place to show that, not a disabled button
//     on the home page.
//   - single-variant products either add directly or report sold out.
export function quickAddAction(variants) {
  if (variants.length > 1) return 'pdp';
  return pickDefaultVariant(variants) ? 'add' : 'soldout';
}

// Free-shipping progress. The threshold lives in shop currency (USD cents),
// the cart total in the customer's currency, so convert the threshold with
// Shopify.currency.rate first. Checkout converts the shipping-rate condition
// the same way.
export function freeShipState(baseThreshold, rate, total) {
  const r = Number(rate) > 0 ? Number(rate) : 1;
  const threshold = Math.round(baseThreshold * r);
  const left = Math.max(0, threshold - total);
  const pct = Math.min(100, Math.floor((total * 100) / threshold));
  return { left, pct };
}

export async function addToCart(variantId, quantity, sourceEl) {
  const sectionIds = [...document.querySelectorAll('[data-hydration-key="cart-drawer-inner"]')]
    .map((el) => el.closest('.shopify-section')?.id?.replace('shopify-section-', ''))
    .filter(Boolean);
  const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }], sections: sectionIds.join(',') }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
  const cart = await (await fetch((window.Shopify?.routes?.root || '/') + 'cart.js')).json();
  try {
    const { CartAddEvent } = await import('@theme/events');
    sourceEl.dispatchEvent(
      new CartAddEvent(cart, sourceEl.id || 'gm-quick-add', {
        source: 'gm-quick-add',
        itemCount: quantity,
        variantId: String(variantId),
        sections: data.sections,
      }),
    );
  } catch {
    document.dispatchEvent(
      new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, data: { sections: data.sections } } }),
    );
  }
  return cart;
}

// <gm-freeship> re-renders its message in the customer's currency. Liquid
// can't convert the USD threshold, so the server text is only right for USD.
// connectedCallback also runs when a cart section re-render inserts a new bar.
if (typeof customElements !== 'undefined' && !customElements.get('gm-freeship')) {
  customElements.define('gm-freeship', class extends HTMLElement {
    connectedCallback() {
      const cur = window.Shopify?.currency;
      if (!cur || cur.active === this.dataset.shopCurrency) return;
      const { left, pct } = freeShipState(Number(this.dataset.threshold), cur.rate, Number(this.dataset.total));
      const fmt = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur.active });
      const msg = this.querySelector('p');
      if (left > 0) {
        // data-away holds the translated sentence with an {amount} marker (locales: blockhead.cart.*).
        const [before, after = ''] = (this.dataset.away || "You're {amount} away from free shipping.").split('{amount}');
        msg.textContent = before;
        msg.append(Object.assign(document.createElement('b'), { textContent: fmt.format(left / 100) }), after);
      } else {
        msg.replaceChildren(Object.assign(document.createElement('b'), { textContent: this.dataset.unlocked || 'Free shipping unlocked.' }));
      }
      this.querySelector('.gm-freeship__track span').style.width = pct + '%';
    }
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest?.('[data-gm-add]');
    // CHOOSE is a real link to the PDP (server-rendered); let it navigate natively.
    if (!btn || btn.tagName === 'A') return;
    e.preventDefault();
    const live = btn.closest('.gm-tk')?.querySelector('[data-gm-live]');
    const say = (text) => { if (live) live.textContent = text; };
    const variants = JSON.parse(btn.dataset.variants || '[]');
    // Translated labels from snippets/gm-lift-ticket.liquid (locales: blockhead.lift_ticket.*).
    const t = {
      soldOut: btn.dataset.tSoldOut || 'Sold out',
      adding: btn.dataset.tAdding || 'Adding…',
      added: btn.dataset.tAdded || 'On the lift ✓',
      addedLive: btn.dataset.tAddedLive || 'Added to your lift pass',
      retry: btn.dataset.tRetry || 'Try again',
      retryLive: btn.dataset.tRetryLive || 'Could not add it. Try again.',
    };
    const action = quickAddAction(variants);
    // Multi-variant products always go to the PDP, checked BEFORE the
    // sold-out branch below: with the store-wide `available: false` DSers
    // location issue, a naive "pick a variant, bail if none available"
    // ordering would show every multi-variant CHOOSE button as "Sold out"
    // instead of letting the customer pick a variant on the PDP.
    if (action === 'pdp') {
      location.href = btn.dataset.pdp;
      return;
    }
    if (action === 'soldout') {
      btn.textContent = t.soldOut;
      btn.disabled = true;
      say(t.soldOut);
      return;
    }
    const id = pickDefaultVariant(variants);
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = t.adding;
    say(t.adding);
    try {
      await addToCart(id, 1, btn);
      btn.textContent = t.added;
      say(t.addedLive);
    } catch (err) {
      btn.textContent = t.retry;
      say(t.retryLive);
      console.error(err);
    }
    setTimeout(() => {
      btn.textContent = label;
      btn.disabled = false;
    }, 1600);
  });
}
